<template>
  <v-dialog
    v-model="dialogModel"
    :fullscreen="isMobile"
    :max-width="isMobile ? '100%' : '800'"
    scrollable
    content-class="filters-dialog"
    transition="dialog-bottom-transition"
  >
    <v-card :class="{ 'mobile-dialog': isMobile }">
      <v-card-title class="d-flex justify-space-between align-center pa-6">
        <span class="text-h6">{{ $t("filters") }}</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 pb-6">
        <v-container class="pa-0">
          <h3 v-if="isMobile" class="text-subtitle-1 my-2">
            {{ $t("guests") }}
          </h3>

          <v-row v-if="isMobile" class="mb-4">
            <v-col cols="6">
              <span class="me-6">{{ $t("adults") }}</span>
              <count-picker :value.sync="localAdults" />
            </v-col>
            <v-col cols="6">
              <span class="me-2">{{ $t("children") }}</span>
              <count-picker :value.sync="localChildren" :min="0" />
            </v-col>
          </v-row>

          <!-- Beds & Baths -->
          <div class="mb-8">
            <h3 class="text-subtitle-1 mb-6">{{ $t("rooms") }}</h3>
            <v-row class="beds-baths-row">
              <v-col cols="6" class="beds-baths-col">
                <v-select
                  v-model="localBeds"
                  :items="bedsOptions"
                  :label="$t('bedrooms')"
                  outlined
                  dense
                  hide-details
                  class="beds-baths-select"
                >
                  <template #prepend-inner>
                    <v-icon class="mr-2 gentle-icon" color="blue-grey lighten-1"
                      >mdi-bed</v-icon
                    >
                  </template>
                </v-select>
              </v-col>
              <v-col cols="6" class="beds-baths-col">
                <v-select
                  v-model="localBaths"
                  :items="bathsOptions"
                  :label="$t('bathrooms')"
                  outlined
                  dense
                  hide-details
                  class="beds-baths-select"
                >
                  <template #prepend-inner>
                    <v-icon class="mr-2 gentle-icon" color="blue-grey lighten-1"
                      >mdi-shower</v-icon
                    >
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </div>
          <div class="mb-8">
            <div class="d-flex justify-space-between align-center mb-6">
              <h3 class="text-subtitle-1">{{ $t("amenities") }}</h3>
              <v-btn
                text
                color="primary"
                @click="clearAmenities"
                v-if="hasActiveAmenities"
                class="text-none px-0"
              >
                {{ $t("clear_all") }} ({{ localAmenities.length }})
              </v-btn>
            </div>
            <div class="subtitle-2 mb-4">{{ $t("popular") }}</div>
            <v-row>
              <v-col
                cols="12"
                sm="6"
                v-for="amenity in computedPopularAmenities"
                :key="amenity.value"
              >
                <v-checkbox
                  v-model="localAmenities"
                  :value="amenity.value"
                  hide-details
                  class="mb-2"
                >
                  <template v-slot:label>
                    <div class="d-flex align-center">
                      <v-icon
                        small
                        class="mr-2 gentle-icon"
                        color="blue-grey lighten-1"
                        >{{ amenity.icon }}</v-icon
                      >
                      <span>{{ $t(amenity.label) }}</span>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>

            <v-expand-transition>
              <div v-show="showMoreAmenities">
                <div class="subtitle-2 font-weight-bold my-4">
                  {{ $t("other_amenities") }}
                </div>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                    v-for="amenity in otherAmenities"
                    :key="amenity.value"
                  >
                    <v-checkbox
                      v-model="localAmenities"
                      :value="amenity.value"
                      hide-details
                      class="mb-2"
                    >
                      <template v-slot:label>
                        <div class="d-flex align-center">
                          <v-icon
                            small
                            class="mr-2 gentle-icon"
                            color="blue-grey lighten-1"
                            >{{ amenity.icon }}</v-icon
                          >
                          <span>{{ $t(amenity.label) }}</span>
                        </div>
                      </template>
                    </v-checkbox>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
            <v-btn
              text
              color="primary"
              class="mt-4 text-none px-0"
              @click="showMoreAmenities = !showMoreAmenities"
            >
              {{ showMoreAmenities ? "Show Less" : "Show More" }}
            </v-btn>
          </div>

          <!-- Price Range -->
          <div class="mb-8">
            <div class="d-flex justify-space-between align-center mb-4">
              <h3 class="text-subtitle-1">{{ $t("price_range") }}</h3>
              <v-radio-group
                v-model="localPricePerNight"
                class="price-radio-group"
                row
                hide-details
              >
                <v-radio
                  :value="true"
                  :label="$t('per_night')"
                  class="price-radio"
                ></v-radio>
                <v-radio
                  :value="false"
                  :label="$t('entire_stay')"
                  class="price-radio"
                ></v-radio>
              </v-radio-group>
            </div>
            <v-row class="price-range">
              <v-col cols="6">
                <v-text-field
                  v-model.number="localPriceRange[0]"
                  type="number"
                  :label="$t('min_price')"
                  outlined
                  :min="0"
                  dense
                  hide-details
                  class="price-input"
                >
                </v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="localPriceRange[1]"
                  type="number"
                  :label="$t('max_price')"
                  outlined
                  dense
                  :min="0"
                  hide-details
                  class="price-input"
                >
                </v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Property Type -->
          <div class="mb-8">
            <div class="d-flex justify-space-between align-center mb-6">
              <h3 class="text-subtitle-1">{{ $t("property_type") }}</h3>
              <v-btn
                text
                color="primary"
                @click="clearPropertyTypes"
                v-if="hasActivePropertyTypes"
                class="text-none px-0"
              >
                {{ $t("clear_all") }} ({{ localPropertyTypes.length }})
              </v-btn>
            </div>
            <v-row>
              <v-col
                v-for="type in propertyTypes"
                :key="type.value"
                cols="6"
                sm="4"
                md="3"
              >
                <v-card
                  :class="[
                    'property-type-card d-flex flex-column align-center pa-4',
                    { selected: localPropertyTypes.includes(type.value) },
                  ]"
                  outlined
                  hover
                  @click="togglePropertyType(type.value)"
                >
                  <v-icon
                    size="28"
                    class="mb-2 gentle-icon"
                    color="blue-grey lighten-1"
                    >{{ type.icon }}</v-icon
                  >
                  <span class="text-caption text-center">
                    {{ $t(type.labelKey) }}
                  </span>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Amenities -->
        </v-container>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn text @click="clearAllFilters" :disabled="!hasActiveFilters">
          {{ $t("clear_all") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          :color="actionsColor"
          :disabled="!hasActiveFilters"
          dark
          class="px-6"
          small
          elevation="0"
          @click="applyFilters"
        >
          {{ $t("show") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import StylesMixin from "@/mixins/styles-mixin";
import DeviceMixin from "@/mixins/device-mixin";
import CountPicker from "@/components/common/count-picker.vue";

export default {
  name: "listing-filters-dialog",
  components: {
    CountPicker,
  },

  mixins: [StylesMixin, DeviceMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
      required: true,
      default: () => ({
        priceRange: [0, 100000],
        beds: null,
        baths: null,
        propertyTypes: [],
        amenities: [],
      }),
    },
    totalListings: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    showMoreAmenities: false,
    localPriceRange: [null, null],
    localPricePerNight: false,
    localBeds: null,
    localBaths: null,
    localPropertyTypes: [],
    localAmenities: [],
    localAdults: 1,
    localChildren: 0,
    hadPreviousFilters: false,
    propertyTypes: [
      { labelKey: "villa", value: "villa", icon: "mdi-home-city" },
      { labelKey: "loft", value: "loft", icon: "mdi-warehouse" },
      {
        labelKey: "condominium",
        value: "condominium",
        icon: "mdi-office-building",
      },
      { labelKey: "apartment", value: "apartment", icon: "mdi-home-modern" },
      { labelKey: "house", value: "house", icon: "mdi-home" },
      { labelKey: "bungalow", value: "bungalow", icon: "mdi-home-floor-1" },
      { labelKey: "other", value: "other", icon: "mdi-home-variant" },
    ],
    popularAmenities: [
      { label: "amenity_pool", value: "Pool", icon: "mdi-pool" },
      {
        label: "amenity_free_parking",
        value: "Free Parking",
        icon: "mdi-parking",
      },
      {
        label: "amenity_air_conditioning",
        value: "air conditioning",
        icon: "mdi-air-conditioner",
      },
      {
        label: "amenity_wireless_internet",
        value: "Wireless Internet",
        icon: "mdi-wifi",
      },
      { label: "amenity_kitchen", value: "Kitchen", icon: "mdi-stove" },
      { label: "amenity_tv", value: "TV", icon: "mdi-television" },
      {
        label: "amenity_washing_machine",
        value: "Washing Machine",
        icon: "mdi-washing-machine",
      },
      {
        label: "amenity_private_bathroom",
        value: "private bathroom",
        icon: "mdi-shower",
      },
    ],
    otherAmenities: [
      { label: "amenity_dryer", value: "Dryer", icon: "mdi-tumble-dryer" },
      {
        label: "amenity_laptop_workspace",
        value: "Laptop workspace",
        icon: "mdi-laptop",
      },
      { label: "amenity_iron", value: "Iron", icon: "mdi-iron" },
      {
        label: "amenity_hair_dryer",
        value: "Hair Dryer",
        icon: "mdi-hair-dryer",
      },
      {
        label: "amenity_bed_linen_towels",
        value: "Bed Linen & Towels",
        icon: "mdi-bed",
      },
      {
        label: "amenity_dishwasher",
        value: "Dishwasher",
        icon: "mdi-dishwasher",
      },
      {
        label: "amenity_coffee_maker",
        value: "coffee maker",
        icon: "mdi-coffee-maker",
      },
      {
        label: "amenity_garden_or_backyard",
        value: "garden or backyard",
        icon: "mdi-tree",
      },
      { label: "amenity_balcony", value: "Balcony", icon: "mdi-balcony" },
      { label: "amenity_hot_tub", value: "Hot Tub", icon: "mdi-hot-tub" },
      { label: "amenity_bbq_grill", value: "BBQ grill", icon: "mdi-grill" },
      { label: "amenity_gym", value: "Gym", icon: "mdi-dumbbell" },
    ],
  }),
  computed: {
    dialogModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    computedPopularAmenities() {
      const amenities = [];
      if ([80, 87, 1].includes(this.settings.tenant_id)) {
        amenities.push({
          label: "Shelter",
          value: "Shelter",
          icon: "mdi-shield-home",
        });
      }
      amenities.push({
        label: "Pet Friendly",
        value: "pet_friendly",
        icon: "mdi-paw",
      });
      return [...amenities, ...this.popularAmenities];
    },
    hasActiveFilters() {
      return (
        this.hasActivePropertyTypes ||
        this.hasActiveAmenities ||
        this.localBeds !== null ||
        this.localBaths !== null ||
        this.localPriceRange[0] > 0 ||
        this.localPriceRange[1] !== null ||
        this.localAdults !== 1 ||
        this.localChildren !== 0 ||
        this.hadPreviousFilters
      );
    },
    hasActivePropertyTypes() {
      return this.localPropertyTypes.length > 0;
    },
    hasActiveAmenities() {
      return this.localAmenities.length > 0;
    },
    bedsOptions() {
      return [
        { text: this.$t("any"), value: null },
        { text: "Studio", value: "studio" },
        { text: "1+", value: 1 },
        { text: "2+", value: 2 },
        { text: "3+", value: 3 },
        { text: "4+", value: 4 },
        { text: "5+", value: 5 },
      ];
    },
    bathsOptions() {
      return [
        { text: this.$t("any"), value: null },
        { text: "1", value: 1 },
        { text: "2", value: 2 },
        { text: "3", value: 3 },
        { text: "4", value: 4 },
        { text: "5+", value: 5 },
      ];
    },
  },
  created() {
    // Remove debounced fetch since we'll only fetch on apply
  },
  methods: {
    ...mapActions("listingsModule", ["getListings"]),

    togglePropertyType(value) {
      const index = this.localPropertyTypes.indexOf(value);
      if (index === -1) {
        this.localPropertyTypes.push(value);
      } else {
        this.localPropertyTypes.splice(index, 1);
      }
    },
    clearPropertyTypes() {
      this.localPropertyTypes = [];
    },
    clearAmenities() {
      this.localAmenities = [];
    },
    updateLocalGuests(guestsData) {
      this.localAdults = guestsData.adults;
      this.localChildren = guestsData.children;
    },
    clearAllFilters() {
      this.localPriceRange = [null, null];
      this.localPricePerNight = false;
      this.localBeds = null;
      this.localBaths = null;
      this.localAdults = 1;
      this.localChildren = 0;
      this.clearPropertyTypes();
      this.clearAmenities();
    },
    closeDialog() {
      this.dialogModel = false;
    },
    applyFilters() {
      this.$emit("apply", {
        priceRange: this.localPriceRange,
        pricePerNight: this.localPricePerNight,
        beds: this.localBeds,
        baths: this.localBaths,
        propertyTypes: [...this.localPropertyTypes],
        amenities: [...this.localAmenities],
        adults: this.localAdults,
        children: this.localChildren,
      });
      this.hadPreviousFilters = false;
      this.closeDialog();
    },
    initializeLocalFilters() {
      this.hadPreviousFilters =
        this.filters.propertyTypes.length > 0 ||
        this.filters.amenities.length > 0 ||
        this.filters.beds !== null ||
        this.filters.baths !== null ||
        this.filters.priceRange[0] > 0 ||
        this.filters.priceRange[1] !== null ||
        (this.filters.adults && this.filters.adults !== 1) ||
        (this.filters.children && this.filters.children !== 0);

      this.localPriceRange = [...this.filters.priceRange];
      this.localPricePerNight = this.filters.pricePerNight === true;
      this.localBeds = this.filters.beds;
      this.localBaths = this.filters.baths;
      this.localPropertyTypes = [...this.filters.propertyTypes];
      this.localAmenities = [...this.filters.amenities];
      this.localAdults = this.filters.adults || 1;
      this.localChildren = this.filters.children || 0;
    },
  },
  watch: {
    filters: {
      handler: "initializeLocalFilters",
      immediate: true,
      deep: true,
    },
    value(newVal) {
      if (newVal) {
        // Initialize filters when dialog opens
        this.initializeLocalFilters();
      }
    },
  },
  beforeDestroy() {
    // Remove debounced fetch cleanup since it's no longer needed
  },
};
</script>

<style lang="scss" scoped>
.v-dialog {
  border-radius: 12px;
  overflow: hidden;

  &.v-dialog--fullscreen {
    border-radius: 0;
  }
}

.mobile-dialog {
  height: 100%;
  display: flex;
  flex-direction: column;

  .v-card__title {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .v-card__text {
    flex: 1;
    overflow-y: auto;
  }

  .v-card__actions {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    padding: 16px !important;
  }
}

.text-h6 {
  font-size: 22px !important;
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.95) !important;
}

.text-subtitle-1 {
  font-size: 16px !important;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.subtitle-2 {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.price-range {
  padding: 0;

  .price-input {
    width: 100%;

    :deep(.v-text-field__slot) {
      input {
        font-size: 15px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);

        &::placeholder {
          color: rgba(0, 0, 0, 0.6);
          font-weight: 400;
        }

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }

    :deep(.v-input__prepend-inner) {
      margin-right: 4px;
      font-size: 15px;
      color: rgba(0, 0, 0, 0.6);
    }

    &.v-text-field--outlined {
      :deep(.v-input__control) {
        .v-input__slot {
          min-height: 40px;
          background-color: white !important;
          border-color: rgba(0, 0, 0, 0.12);

          &:hover {
            border-color: rgba(0, 0, 0, 0.38);
          }

          &:focus-within {
            border-color: var(--v-actions-base) !important;
            border-width: 2px;
          }
        }
      }
    }

    // Active state when value is not default
    &.v-text-field--outlined :deep(.v-input__control) {
      .v-input__slot {
        &:has(input[value]:not([value="0"]):not([value="100000"])) {
          border-color: var(--v-actions-base) !important;
          border-width: 2px;
        }
      }
    }
  }
}

// Style for select inputs (beds and baths)
:deep(.v-select.v-input--is-focused) {
  .v-input__slot {
    border-color: var(--v-actions-base) !important;
    border-width: 2px !important;
  }
}

:deep(.v-select) {
  .v-input__slot {
    border-color: rgba(0, 0, 0, 0.12) !important;

    &:hover {
      border-color: rgba(0, 0, 0, 0.38) !important;
    }
  }

  &.v-select--is-selecting .v-input__slot,
  &:has(.v-select__selection:not(:empty)) .v-input__slot {
    border-color: var(--v-actions-base) !important;
    border-width: 2px !important;
  }
}

.property-type-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.22);
  height: 90px;
  border-radius: 8px !important;
  background-color: white !important;

  .v-icon {
    opacity: 0.9;
    transition: all 0.2s ease;
    color: rgba(0, 0, 0, 0.87);
    font-size: 28px !important;
  }

  .text-caption {
    font-size: 13px !important;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.95);
    margin-top: 4px;
  }

  &:hover {
    border-color: rgba(0, 0, 0, 0.87);
    background-color: rgb(245, 245, 245) !important;

    .v-icon {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  &.selected {
    background-color: rgb(245, 245, 245) !important;
    border-color: rgba(0, 0, 0, 0.87);
    border-width: 2px;

    .v-icon {
      opacity: 1;
      color: var(--v-primary-base);
    }
  }
}

:deep(.v-input--selection-controls) {
  margin-top: 0;
  padding-top: 0;

  .v-input__slot {
    margin-bottom: 0;
  }

  .v-input--selection-controls__input {
    margin-right: 8px;
  }

  .v-label {
    font-size: 15px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.95) !important;
  }

  .v-icon {
    opacity: 0.9;
    color: rgba(0, 0, 0, 0.87);
  }

  &.v-input--is-label-active {
    .v-icon {
      opacity: 1;
      color: var(--v-primary-base);
    }
  }

  .v-input--selection-controls__ripple {
    &:before {
      background-color: var(--v-primary-base) !important;
      opacity: 0.12;
    }
  }
}

.v-card-actions {
  border-top: 1px solid rgba(0, 0, 0, 0.12);

  .v-btn {
    letter-spacing: 0.5px;
    text-transform: none;
    font-weight: 500;
    border-radius: 6px;

    &.v-btn--disabled {
      opacity: 0.5 !important;
    }

    &.primary {
      height: 36px;
      font-size: 14px;

      &:not(.v-btn--disabled) {
        background-color: var(--v-primary-base) !important;
        color: white !important;
      }

      &:hover:not(.v-btn--disabled) {
        background-color: var(--v-primary-darken1) !important;
      }
    }
  }
}

.text-none {
  text-transform: none !important;
  letter-spacing: normal !important;
  font-weight: 500 !important;
}

:deep(.v-messages) {
  min-height: 0;
}

// Override Vuetify's default disabled button opacity
:deep(.v-btn.v-btn--disabled) {
  opacity: 0.5 !important;
}

// Make primary color more vibrant
:deep(.primary) {
  &.v-btn:not(.v-btn--text) {
    opacity: 1 !important;
  }
}

// Override checkbox colors
:deep(.v-input--selection-controls__input) {
  .v-icon {
    &.primary--text {
      color: var(--v-primary-base) !important;
    }
  }
}

.beds-baths-select {
  min-width: 180px;
  width: 100%;
  margin-bottom: 0 !important;
}
:deep(.v-list-item__content) {
  padding: 8px !important;
}

.gentle-icon {
  opacity: 0.85 !important;
  filter: brightness(1.08);
  transition: opacity 0.2s, filter 0.2s;
}

.gentle-icon:hover {
  opacity: 1 !important;
  filter: brightness(1.18);
}
.mobile-guests-button {
  width: 100% !important;
  min-width: 100% !important;
}

.price-radio-group {
  margin: 0 !important;

  :deep(.v-input__slot) {
    margin-bottom: 0 !important;
  }

  .price-radio {
    margin-right: 16px !important;

    :deep(.v-label) {
      font-size: 14px !important;
      font-weight: 500 !important;
      color: rgba(0, 0, 0, 0.87) !important;
    }

    :deep(.v-icon) {
      color: var(--v-primary-base) !important;
    }
  }
}
</style>
